import { ILegalClient } from '../../Interfaces/ILegalClient';
import { ILegalTransactionRelationshipMember } from '../../Interfaces/ILegalTransactionRelationship/ILegalTransactionRelationshipMember';
import { ILegalTransactionRelationshipRealEstate } from '../../Interfaces/ILegalTransactionRelationship/ILegalTransactionRelationshipRealEstate/ILegalTransactionRelationshipRealEstate';
import { ILegalTransactionRelationshipRealEstatePropertyType } from '../../Interfaces/ILegalTransactionRelationship/ILegalTransactionRelationshipRealEstate/ILegalTransactionRelationshipRealEstatePropertyType';

/**
 * Updates client names in the original array with data from the new array.
 * This is used to provide a live upload on the client names if they are updated.
 * @param originalArray The original array of legal clients to update
 * @param newArray The new array containing updated client information
 * @returns A new array with updated client names
 */
export const updateNames = (
  originalArray: ILegalTransactionRelationshipMember[],
  newArray: ILegalClient[]
): ILegalTransactionRelationshipMember[] => {
  const newMembersMap = new Map(
    newArray.map(member => [member.idLegalClient, member])
  );

  return originalArray.map(member => {
    const newMember = newMembersMap.get(member.idLegalClient);
    if (newMember) {
      return {
        ...member,
        FirstName: newMember.FirstName,
        LastName: newMember.LastName,
        LegalClient: {
          ...member.LegalClient,
          FirstName: newMember.FirstName,
          LastName: newMember.LastName,
        },
      };
    }
    return member;
  });
};

/**
 * Removes the first manager with the specified ID and relationship role 3 from the array.
 * @param managers Array of relationship members to search through
 * @param idToRemove The ID of the legal client to remove
 * @returns The updated array with the manager removed (if found)
 */
export const removeFirstManager = (
  managers: ILegalTransactionRelationshipMember[],
  idToRemove: number
) => {
  const index = managers.findIndex(
    c => c.idLegalClient === idToRemove && c.idRelationshipRole === 3
  );
  if (index !== -1) {
    managers.splice(index, 1);
  }
  return managers;
};

export const getNextStep = (
  step: number,
  propertyTypes: ILegalTransactionRelationshipRealEstatePropertyType[]
) => {
  if (step < 3) return step + 1;
  if (step === 3) {
    if (propertyTypes.length === 1 && propertyTypes[0].idPropertyType === 2) {
      return 5; // Skip step 4
    } else {
      return 4;
    }
  }
  if (step === 4) return 5;
  if (step < 7) return step + 1;
  return step; // Stay at step 7 (last step)
};

export const getPreviousStep = (
  step: number,
  propertyTypes: ILegalTransactionRelationshipRealEstatePropertyType[]
) => {
  if (step > 5) return step - 1;
  if (step === 5) {
    if (propertyTypes.length === 1 && propertyTypes[0].idPropertyType === 2) {
      return 3; // Go back to step 3 if step 4 was skipped
    } else {
      return 4;
    }
  }
  if (step > 0) return step - 1;
  return step; // Stay at step 0 (first step)
};

export const validateAddress = ({
  Street,
  StreetNr,
  idPostcode,
  Latitude,
  Longitude,
}: ILegalTransactionRelationshipRealEstate): boolean =>
  Boolean(
    (Street && StreetNr && idPostcode && idPostcode > 0) ||
      (Latitude && Longitude)
  );

export const validateArea = (
  {
    hasPartialArea,
    isMeasured,
    Area,
  }: Pick<
    ILegalTransactionRelationshipRealEstate,
    'hasPartialArea' | 'isMeasured' | 'Area'
  >,
  pc?: {
    PaymentMeasurement_idPurchaseContractRelationType?: number | null;
    OrderMeasurement_idPurchaseContractRelationType?: number | null;
  }
) =>
  !hasPartialArea ||
  (hasPartialArea &&
    (pc?.PaymentMeasurement_idPurchaseContractRelationType ?? null) !== null &&
    ((isMeasured && Area !== null) ||
      (!isMeasured &&
        (pc?.OrderMeasurement_idPurchaseContractRelationType ?? null) !==
          null)));
